(() => {
  window.ACHomeMotion?.destroy?.();

  const state = {
    cleanups: [], context: null, booted: false,
    lifecycle: new AbortController(), listening: false, observer: null,
    bootTimer: 0, generation: 0, focusedControl: null,
    sceneTimer: 0, completeScenes: null,
  };
  const query = (selector, root = document) => root.querySelector(selector);
  const queryAll = (selector, root = document) => [...root.querySelectorAll(selector)];
  const isHome = () => document.body?.classList.contains('ac-reference--home');
  const prefersReducedMotion = () => document.body?.dataset.acMotion === 'reduced'
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const addCleanup = (cleanup) => state.cleanups.push(cleanup);
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const modulo = (value, size) => ((value % size) + size) % size;
const interactiveSelector = 'a, button, input, select, textarea, label, form, summary, [role="button"], [contenteditable="true"]';
const isInteractiveTarget = (target) => target instanceof Element && Boolean(target.closest(interactiveSelector));

  const resetScenes = () => {
    if (document.activeElement !== document.body) state.focusedControl = document.activeElement;
    state.generation++;
    window.clearTimeout(state.sceneTimer);
    state.sceneTimer = 0;
    state.completeScenes?.(false);
    state.completeScenes = null;
    window.clearTimeout(state.bootTimer);
    state.bootTimer = 0;
    state.context?.revert?.();
    state.context = null;
    state.cleanups.splice(0).reverse().forEach((cleanup) => {
      try { cleanup(); } catch (error) { /* A detached Theme Editor section is safe to ignore. */ }
    });
    state.booted = false;
  };

  const destroy = () => {
    resetScenes();
    state.lifecycle.abort();
    state.observer?.disconnect();
    state.observer = null;
    state.listening = false;
    state.focusedControl = null;
  };

  const queueBoot = () => {
    if (state.lifecycle.signal.aborted) return;
    window.clearTimeout(state.bootTimer);
    state.bootTimer = window.setTimeout(boot, 0);
  };

  const connectLifecycle = () => {
    if (state.listening) return;
    if (state.lifecycle.signal.aborted) state.lifecycle = new AbortController();
    const { signal } = state.lifecycle;
    state.listening = true;
    document.addEventListener('shopify:section:load', queueBoot, { signal });
    document.addEventListener('shopify:section:unload', () => {
      resetScenes();
      queueBoot();
    }, { signal });
    for (const media of [
      '(prefers-reduced-motion: reduce)',
      '(min-width: 1024px) and (hover: hover) and (pointer: fine)',
    ]) window.matchMedia(media).addEventListener('change', queueBoot, { signal });
    state.observer = new MutationObserver(queueBoot);
    state.observer.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
  };

  // Shared fold reveal; callers retain their trigger, origin and timing.
  const revealFold = (gsap, targets, { origin = 'bottom center', ...options } = {}) =>
    gsap.fromTo(targets, { scaleY: 0, transformOrigin: origin }, {
      scaleY: 1, duration: .7, ease: 'power2.out', ...options,
    });

  const initHero = (gsap, ScrollTrigger, reduced) => {
    const hero = query('[data-ac-ref-hero]');
    const slides = hero ? queryAll('[data-ac-ref-hero-slide]', hero) : [];
    if (!hero || !slides.length) return;

    const events = new AbortController();
    const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: events.signal });
    const titles = slides.map((slide) => queryAll('[data-ac-ref-hero-title]', slide));
    const rotationControl = query('[data-ac-ref-hero-rotation]', hero);
    const slideRegion = query('.ac-ref-hero__slides', hero);
    const pagination = query('[data-ac-ref-hero-pagination]', hero);
    const dots = queryAll('[data-ac-ref-hero-dot]', hero);
    const previousControl = query('[data-ac-ref-hero-prev]', hero);
    const nextControl = query('[data-ac-ref-hero-next]', hero);
    const delay = Number(hero.dataset.delay || 5000);
    // GSAP did not parse the old CSS cubic-bezier string and used this default.
    const ease = 'power1.out';
    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timeout = 0;
    let transition = null;
    let busy = false;
    let heroVisible = true;
    let rotationPaused = reduced || rotationControl?.dataset.acRotationChoice === 'pause' || hero.contains(document.activeElement);
    let hoverPaused = window.matchMedia('(hover: hover)').matches && hero.matches(':hover');
    let holdTransition = false;
    let pointerWantsRotation = null;
    let pendingSelection = null;
    let gesture = null;
    let suppressClick = false;

    const renderClasses = (active, next = -1) => {
      slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === active);
        slide.classList.toggle('is-next', index === next);
        slide.inert = index !== active;
        slide.setAttribute('aria-hidden', String(index !== active));
      });
      dots.forEach((dot, index) => dot.setAttribute('aria-pressed', String(index === active)));
    };
    const settle = (index) => {
      current = modulo(index, slides.length);
      renderClasses(current);
      if (gsap) slides.forEach((slide, index) => {
        gsap.set([slide, ...titles[index]], { scaleY: index === current ? 1 : 0, transformOrigin: '50% 100%' });
      });
    };
    const schedule = () => {
      window.clearTimeout(timeout);
      if (rotationControl) {
        rotationControl.dataset.paused = String(rotationPaused);
        rotationControl.setAttribute('aria-label', rotationPaused ? rotationControl.dataset.playLabel : rotationControl.dataset.pauseLabel);
      }
      if (busy) transition?.paused(holdTransition || document.hidden || !heroVisible);
      const stopped = reduced || rotationPaused || hoverPaused || document.hidden || !heroVisible || slides.length < 2;
      slideRegion?.setAttribute('aria-live', stopped ? 'polite' : 'off');
      if (!stopped && !busy) timeout = window.setTimeout(() => change(current + 1), delay);
    };
    const pauseRotation = (hold = false) => {
      rotationPaused = true;
      holdTransition = hold;
      if (rotationControl) rotationControl.dataset.acRotationChoice = 'pause';
      schedule();
    };
    const completeTransition = (index) => {
      busy = false;
      settle(index);
      const pending = pendingSelection;
      pendingSelection = null;
      if (pending) selectSlide(pending.index, pending.direction);
      else schedule();
    };
    const change = (index) => {
      index = modulo(index, slides.length);
      if (busy || slides.length < 2 || index === current) return;
      // The shared post-load queue warms templates without mounting hidden slides.
      for (const template of slides[index].querySelectorAll('template[data-ac-hero-image]')) {
        const image = template.content.querySelector('img');
        image.loading = 'eager';
        image.fetchPriority = 'auto';
        template.replaceWith(template.content);
      }
      if (!gsap || reduced) { settle(index); schedule(); return; }
      const outgoing = slides[current];
      const incoming = slides[index];
      busy = true;
      renderClasses(current, index);
      gsap.set([incoming, ...titles[index]], { scaleY: 0, transformOrigin: '50% 100%' });
      transition?.kill();
      transition = gsap.timeline({ defaults: { overwrite: true }, onComplete: () => completeTransition(index) })
        .to(outgoing, { scaleY: 0, duration: 1.5, ease, transformOrigin: '50% 0%' }, 0)
        .to(incoming, { scaleY: 1, duration: 1.5, ease, transformOrigin: '50% 100%' }, 0)
        .to(titles[current], { scaleY: 0, duration: .7, ease, transformOrigin: '50% 0%' }, 0)
        .to(titles[index], { scaleY: 1, duration: .7, ease, transformOrigin: '50% 100%' }, .8);
      schedule();
    };
    const selectSlide = (index, direction) => {
      pauseRotation();
      if (busy) pendingSelection = { index, direction };
      else change(index ?? current + direction);
    };

    const cancelGesture = () => {
      if (gesture?.pointer && slideRegion.hasPointerCapture(gesture.id)) slideRegion.releasePointerCapture(gesture.id);
      gesture = null;
      suppressClick = false;
    };
    const startGesture = (id, x, y, pointer = false) => {
      gesture = { id, x, y, pointer, swiping: false };
      pauseRotation();
    };
    const moveGesture = (x, y) => {
      if (gesture.swiping) return true;
      x = Math.abs(x - gesture.x);
      y = Math.abs(y - gesture.y);
      if (y > 8 && y > x) { gesture = null; suppressClick = true; return false; }
      if (x < 8 || x <= y * 1.2) return false;
      gesture.swiping = true;
      suppressClick = true;
      return true;
    };
    const endGesture = (x, y) => {
      const start = gesture;
      gesture = null;
      if (!start?.swiping) return;
      x -= start.x;
      if (Math.abs(x) >= 40 && Math.abs(x) > Math.abs(y - start.y) * 1.2) selectSlide(null, x < 0 ? 1 : -1);
    };
    on(slideRegion, 'pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      cancelGesture();
      if (event.isPrimary && event.button === 0 && slides.length > 1) startGesture(event.pointerId, event.clientX, event.clientY, true);
    });
    on(slideRegion, 'pointermove', (event) => {
      if (!gesture?.pointer || gesture.id !== event.pointerId) return;
      if (moveGesture(event.clientX, event.clientY) && !slideRegion.hasPointerCapture(event.pointerId)) slideRegion.setPointerCapture(event.pointerId);
    });
    on(slideRegion, 'pointerup', (event) => {
      if (gesture?.pointer && gesture.id === event.pointerId) endGesture(event.clientX, event.clientY);
    });
    on(slideRegion, 'pointercancel', (event) => { if (event.pointerType !== 'touch') cancelGesture(); });
    // Touch and pointer inputs share one gesture; Safari pointercancel cannot discard an ongoing touch.
    on(slideRegion, 'touchstart', (event) => {
      cancelGesture();
      if (event.touches.length !== 1 || slides.length < 2) return;
      const touch = event.touches[0];
      startGesture(touch.identifier, touch.clientX, touch.clientY);
    }, { passive: true });
    on(slideRegion, 'touchmove', (event) => {
      if (event.touches.length !== 1) { cancelGesture(); return; }
      if (!gesture || gesture.pointer) return;
      const touch = event.touches[0];
      if (touch.identifier === gesture.id && moveGesture(touch.clientX, touch.clientY) && event.cancelable) event.preventDefault();
    }, { passive: false });
    on(slideRegion, 'touchend', (event) => {
      if (!gesture || gesture.pointer) return;
      const touch = [...event.changedTouches].find((item) => item.identifier === gesture.id);
      if (touch) endGesture(touch.clientX, touch.clientY);
    });
    on(slideRegion, 'touchcancel', cancelGesture);
    on(slideRegion, 'dragstart', (event) => event.preventDefault());
    on(slideRegion, 'click', (event) => {
      if (!suppressClick || event.detail === 0) return;
      suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true });

    on(pagination, 'click', (event) => {
      const dot = event.target.closest('[data-ac-ref-hero-dot]');
      if (dot) selectSlide(Number(dot.dataset.acRefHeroDot));
    });
    on(previousControl, 'click', () => selectSlide(null, -1));
    on(nextControl, 'click', () => selectSlide(null, 1));
    on(rotationControl, 'pointerdown', () => { pointerWantsRotation = rotationPaused; });
    on(rotationControl, 'pointercancel', () => { pointerWantsRotation = null; });
    on(rotationControl, 'click', (event) => {
      const shouldPlay = event.detail === 0 ? rotationPaused : (pointerWantsRotation ?? rotationPaused);
      pointerWantsRotation = null;
      rotationPaused = reduced || !shouldPlay;
      holdTransition = rotationPaused;
      pendingSelection = null;
      if (rotationControl) rotationControl.dataset.acRotationChoice = shouldPlay ? 'play' : 'pause';
      schedule();
    });
    on(hero, 'focusin', () => pauseRotation(document.documentElement.dataset.acInputMode === 'keyboard'));
    on(hero, 'pointerenter', (event) => { if (event.pointerType !== 'touch') { hoverPaused = true; schedule(); } });
    on(hero, 'pointerleave', (event) => { if (event.pointerType !== 'touch') { hoverPaused = false; schedule(); } });
    on(document, 'visibilitychange', schedule);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      heroVisible = Boolean(entry?.isIntersecting);
      schedule();
    }) : null;
    observer?.observe(hero);
    for (const control of [previousControl, nextControl, ...dots]) if (control) control.disabled = slides.length < 2;
    if (pagination) pagination.hidden = slides.length < 2;
    if (rotationControl) rotationControl.disabled = reduced || slides.length < 2;
    settle(current);
    schedule();

    if (gsap && ScrollTrigger && !reduced) {
      const media = gsap.matchMedia();
      media.add('(min-width: 1024px)', () => {
        const arrivals = query('[data-ac-ref-arrivals]');
        if (!arrivals) return;
        let focusBeforeRefresh = null;
        return ScrollTrigger.create({
          trigger: hero, start: 'top top', endTrigger: arrivals, end: 'top top',
          pin: hero, pinSpacing: false, invalidateOnRefresh: true,
          onRefreshInit: () => { focusBeforeRefresh = hero.contains(document.activeElement) ? document.activeElement : null; },
          onRefresh: () => {
            if (focusBeforeRefresh?.isConnected && !focusBeforeRefresh.disabled && document.activeElement === document.body) focusBeforeRefresh.focus({ preventScroll: true });
            focusBeforeRefresh = null;
          },
        });
      });
      addCleanup(() => media.revert());
    }
    addCleanup(() => {
      events.abort();
      window.clearTimeout(timeout);
      observer?.disconnect();
      pendingSelection = null;
      transition?.kill();
      cancelGesture();
      if (pagination) pagination.hidden = true;
      for (const control of [previousControl, nextControl, rotationControl, ...dots]) if (control) control.disabled = true;
      slideRegion?.setAttribute('aria-live', 'off');
      slides.forEach((slide, index) => gsap?.set([slide, ...titles[index]], { clearProps: 'transform' }));
    });
  };


  const initCampaign = (gsap, ScrollTrigger, reduced) => {
    const section = query('[data-ac-home-campaign]');
    if (!section) return;
    const video = query('video', section);
    if (!gsap || !ScrollTrigger || reduced) return;

    for (const wrapper of queryAll('[data-ac-campaign-lines]', section)) {
      const lines = queryAll('.ac-ref-campaign__line', wrapper);
      if (!lines.length) continue;
      gsap.fromTo(lines, { yPercent: -105 }, { yPercent: 0, duration: .8, ease: 'power2.out', stagger: .08, scrollTrigger: { trigger: wrapper, start: 'top 100%', once: true } });
    }
    for (const element of queryAll('[data-ac-campaign-fold]', section)) {
      revealFold(gsap, element, { ease: 'power1.out', scrollTrigger: { trigger: element, start: 'top 100%', once: true } });
    }
    if (video) {
      gsap.fromTo(video, { yPercent: 10 }, { yPercent: -10, ease: 'none', scrollTrigger: { trigger: section, start: 'top 100%', end: 'bottom top', scrub: true } });
    }
  };

  const initCategoryLoop = (gsap, ScrollTrigger, reduced) => {
    const section = query('[data-ac-ref-categories]');
    if (!section) return;
    const interaction = query('[data-ac-category-interaction]', section);
    if (!interaction) return;
    // Build loop clones before reveal transforms are applied to the originals.
    // Otherwise newly cloned cards can retain an entrance offset after it ends.
    window.ACRails?.init?.(interaction);
    const track = query('[data-ac-rail-track]', interaction);
    if (!track) return;
    if (!gsap || !ScrollTrigger || reduced) return;
    const cards = queryAll('[data-ac-category-card]:not([data-ac-rail-clone])', track);
    const label = query('.ac-ref-section-label', section);
    const action = query('.ac-ref-categories__bottom .ac-action--marker', section);
    const reveal = gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 75%', once: true } });
    if (cards.length) reveal.fromTo(cards, { yPercent: (itemIndex) => [10, 25, 18, 15][itemIndex % 4] }, { yPercent: 0, duration: .8, ease: 'power2.out', stagger: 0 }, 0);
    if (label) reveal.fromTo(label, { scaleY: 0, transformOrigin: 'bottom' }, { scaleY: 1, duration: .8, ease: 'power2.out' }, 0);
    if (action) reveal.fromTo(action, { scaleY: 0, transformOrigin: 'bottom' }, { scaleY: 1, duration: .8, ease: 'power2.out' }, 0);
  };

  const initCategoryEditorial = (gsap, ScrollTrigger, reduced) => {
    const section = query('[data-ac-category-editorial]');
    if (!section) return;
    const stage = query('[data-ac-category-editorial-stage]', section);
    if (!stage) return;

    if (!gsap || !ScrollTrigger || reduced) return;

    const title = query('.ac-category-editorial__header .ac-section-header__title', section);
    const reveals = queryAll('[data-ac-category-editorial-reveal]', stage);
    if (title) {
      revealFold(gsap, title, {
        origin: 'bottom',
        scrollTrigger: { trigger: title, start: 'top 95%', once: true },
      });
    }
    gsap.set(stage, { willChange: 'auto' });
    gsap.fromTo(stage, { clipPath: 'inset(0 15% 0 15%)' }, {
      clipPath: 'inset(0 0% 0 0%)',
      ease: 'none',
      scrollTrigger: {
        trigger: stage,
        start: 'top bottom',
        end: 'top 20%',
        scrub: true,
        invalidateOnRefresh: true,
        onToggle: (self) => { stage.style.willChange = self.isActive ? 'clip-path' : 'auto'; },
      },
    });
    if (reveals.length) {
      revealFold(gsap, reveals, {
        origin: 'bottom',
        stagger: .025,
        scrollTrigger: { trigger: stage, start: 'top 95%', once: true },
      });
    }
  };

  const initBold = (gsap, ScrollTrigger, reduced) => {
    queryAll('[data-ac-ref-bold]').forEach((section) => {
      const artwork = query('[data-ac-ref-bold-artwork]', section);
      const pointerMotion = section.dataset.acRefBoldMotion === 'true';

      if (gsap && ScrollTrigger && !reduced) {
        const reveals = queryAll('.ac-ref-bold__title, .ac-ref-bold__copy, .ac-ref-bold__bottom-icon', section);
        reveals.forEach((element) => {
          revealFold(gsap, element, {
            origin: '50% 100%',
            scrollTrigger: { trigger: element, start: 'top 95%', once: true },
          });
        });

        if (artwork) {
          gsap.fromTo(artwork, {
            clipPath: 'inset(0 100% 0 0)',
          }, {
            clipPath: 'inset(0 0% 0 0)',
            duration: .6,
            ease: 'power2.out',
            scrollTrigger: { trigger: artwork, start: 'top 95%', once: true },
          });
        }
      }

      const fineDesktopPointer = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches;
      if (!gsap || reduced || !pointerMotion || !fineDesktopPointer || !artwork) return;

      let clientX = null;
      let clientY = null;
      let currentX = 0;
      let currentY = 0;
      let targetX = 0;
      let targetY = 0;
      let frame = 0;
      let targetDirty = false;
      let artworkNearby = !('IntersectionObserver' in window);
      const setArtworkX = gsap.quickSetter(artwork, 'x', 'px');
      const setArtworkY = gsap.quickSetter(artwork, 'y', 'px');
      const setArtworkPosition = () => { setArtworkX(currentX); setArtworkY(currentY); };

      const renderArtwork = () => {
        if (!artworkNearby) { frame = 0; return; }
        if (targetDirty) {
          targetDirty = false;
          measureArtworkTarget();
        }
        currentX += (targetX - currentX) * .028;
        currentY += (targetY - currentY) * .028;
        const settled = Math.abs(targetX - currentX) < .05 && Math.abs(targetY - currentY) < .05;
        if (settled) {
          currentX = targetX;
          currentY = targetY;
        }
        setArtworkPosition();
        frame = settled ? 0 : window.requestAnimationFrame(renderArtwork);
      };

      const requestArtworkFrame = () => {
        if (!frame) frame = window.requestAnimationFrame(renderArtwork);
      };

      const measureArtworkTarget = () => {
        if (!artworkNearby || clientX === null || clientY === null) return;
        const rect = section.getBoundingClientRect();
        const artworkWidth = artwork.offsetWidth;
        const artworkHeight = artwork.offsetHeight;
        targetX = clamp((clientX - rect.left - rect.width / 2) * .22, artworkWidth * -.22, artworkWidth * .22);
        targetY = clamp((clientY - rect.top - rect.height / 2) * .22, artworkHeight * -.22, artworkHeight * .22);
      };
      const updateArtworkTarget = () => {
        if (!artworkNearby) return;
        targetDirty = true;
        requestArtworkFrame();
      };

      const onWindowMove = (event) => {
        clientX = event.clientX;
        clientY = event.clientY;
        updateArtworkTarget();
      };
      const onViewportChange = () => updateArtworkTarget();
      const artworkObserver = 'IntersectionObserver' in window
        ? new IntersectionObserver(([entry]) => {
          const wasNearby = artworkNearby;
          artworkNearby = Boolean(entry?.isIntersecting);
          artwork.style.willChange = artworkNearby ? 'transform' : 'auto';
          if (artworkNearby) {
            updateArtworkTarget();
            if (!wasNearby && clientX !== null && clientY !== null) {
              if (frame) window.cancelAnimationFrame(frame);
              frame = 0;
              measureArtworkTarget();
              targetDirty = false;
              currentX = targetX;
              currentY = targetY;
              setArtworkPosition();
            }
          }
          else if (frame) {
            window.cancelAnimationFrame(frame);
            frame = 0;
          }
        }, { rootMargin: '200% 0px' })
        : null;

      gsap.set(artwork, { xPercent: -50, yPercent: -50, x: 0, y: 0, willChange: artworkNearby ? 'transform' : 'auto' });
      artworkObserver?.observe(section);
      window.addEventListener('pointermove', onWindowMove, { passive: true });
      window.addEventListener('scroll', onViewportChange, { passive: true });
      window.addEventListener('resize', onViewportChange);

      addCleanup(() => {
        artworkObserver?.disconnect();
        window.removeEventListener('pointermove', onWindowMove);
        window.removeEventListener('scroll', onViewportChange);
        window.removeEventListener('resize', onViewportChange);
        if (frame) window.cancelAnimationFrame(frame);
        gsap.set(artwork, { clearProps: 'transform,willChange' });
      });
    });
  };

  const initMotionFilm = (gsap, ScrollTrigger, reduced) => {
    const footer = query('[data-ac-ref-footer]');

    for (const section of queryAll('[data-ac-ref-motion]')) {
      const pin = query('[data-ac-ref-motion-pin]', section);
      const mask = query('[data-ac-ref-motion-mask]', section);
      if (!pin || !mask) continue;
      const video = query('video', mask);
      const scrollPrompt = query('.ac-ref-motion__scroll', section);

      if (!gsap || !ScrollTrigger || reduced) continue;

      section.setAttribute('data-ac-motion-pinned', '');
      addCleanup(() => section.removeAttribute('data-ac-motion-pinned'));

      const progressValues = () => {
        if (window.innerWidth < 768) return { from: 90, to: 1000 };
        if (window.innerWidth < 1024) return { from: 60, to: 500 };
        return { from: 30, to: 440 };
      };
      let values = progressValues();
      let previousWidth;
      let previousScale;
      let previousAlpha;
      const setProgress = (progress) => {
        const width = values.from + (values.to - values.from) * progress;
        const scale = 1.2 - .2 * Math.min(progress / .3, 1);
        const alpha = 1 - Math.min(progress / .08, 1);
        if (width !== previousWidth) {
          // Update the mask itself, without inheriting a changing custom property
          // into its video subtree. Shape, percentage, timing and CSS fallback stay.
          mask.style.maskSize = `${width}%`;
          mask.style.webkitMaskSize = `${width}%`;
          previousWidth = width;
        }
        // Video scale and prompt opacity settle early; do not create identical
        // GSAP setters for the rest of the scroll, or during a no-op refresh.
        if (video && scale !== previousScale) {
          gsap.set(video, { scale });
          previousScale = scale;
        }
        if (scrollPrompt && alpha !== previousAlpha) {
          gsap.set(scrollPrompt, { autoAlpha: alpha });
          previousAlpha = alpha;
        }
      };

      setProgress(0);
      const updateProgress = (self) => {
        const distance = self.end - self.start;
        // The mask finishes at footer bottom/bottom; keep its video pinned underneath
        // until footer bottom/top so toolbar resizes cannot expose the clipped seam.
        setProgress(footer ? clamp(self.progress * distance / Math.max(1, distance - window.innerHeight), 0, 1) : self.progress);
      };
      ScrollTrigger.create({
        trigger: pin,
        start: 'top top',
        endTrigger: footer || section,
        end: footer ? 'bottom top' : '+=100%',
        pin,
        pinSpacing: false,
        invalidateOnRefresh: true,
        onRefresh: (self) => { values = progressValues(); updateProgress(self); },
        onUpdate: updateProgress,
      });

      const reveal = queryAll('.ac-ref-motion__header, .ac-ref-motion__icons, .ac-ref-motion__heading-row, .ac-ref-motion__copy, .ac-ref-motion__scroll', section);
      if (reveal.length) {
        revealFold(gsap, reveal, {
          ease: 'power1.out',
          stagger: .06,
          scrollTrigger: { trigger: section, start: 'top 75%', once: true },
        });
      }

      addCleanup(() => {
        mask.style.removeProperty('mask-size');
        mask.style.removeProperty('-webkit-mask-size');
        if (video) gsap.set(video, { clearProps: 'transform' });
        if (scrollPrompt) gsap.set(scrollPrompt, { clearProps: 'opacity,visibility' });
      });
    }
  };

  const boot = () => {
    resetScenes();
    if (!isHome()) return;
    connectLifecycle();
    state.booted = true;
    const reduced = prefersReducedMotion();
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (gsap && ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    const generation = state.generation;
    const signal = state.lifecycle.signal;
    const context = state.context = gsap?.context ? gsap.context(() => {}) : null;
    const live = () => state.booted && state.generation === generation
      && !signal.aborted && state.context === context;
    const run = (init) => {
      if (!live()) return;
      if (context) context.add(() => init(gsap, ScrollTrigger, reduced));
      else init(gsap, ScrollTrigger, reduced);
    };

    // Containment is only an initial-load optimization. Restore ordinary layout
    // before GSAP measures a nearby scene, and keep it ordinary until cleanup.
    const renderSelector = '.ac-ref-campaign, .ac-ref-categories, .ac-category-editorial__stage, .ac-ref-bold';
    const prepareSceneLayout = (family) => {
      for (const section of family.sections) {
        const boxes = section.matches(renderSelector) ? [section] : queryAll(renderSelector, section);
        for (const box of boxes) {
          if (box.hasAttribute('data-ac-layout-ready')) continue;
          box.setAttribute('data-ac-layout-ready', '');
          addCleanup(() => box.removeAttribute('data-ac-layout-ready'));
        }
      }
    };

    // Measure before scene writes. Visible/nearby content must not flash back to a from-state.
    const restorePosition = window.scrollY > 0 || Boolean(window.location.hash)
      || performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    const nearEdge = window.innerHeight * 2;
    const families = [
      [initCampaign, '[data-ac-home-campaign]'],
      [initCategoryLoop, '[data-ac-ref-categories]'],
      [initCategoryEditorial, '[data-ac-category-editorial]'],
      [initBold, '[data-ac-ref-bold]'],
      [initMotionFilm, '[data-ac-ref-motion]'],
    ].map(([init, selector]) => {
      const sections = queryAll(selector);
      return {
        init, sections,
        immediate: restorePosition || reduced || !gsap || !ScrollTrigger || sections.some((section) => {
          const rect = section.getBoundingClientRect();
          return rect.height > 0 && rect.bottom > 0 && rect.top < nearEdge;
        }),
      };
    });
    let complete;
    const scenesReady = new Promise((resolve) => { complete = resolve; });
    let completed = false;
    const finish = (success) => {
      if (completed) return;
      completed = true;
      if (state.completeScenes === finish) state.completeScenes = null;
      complete(success);
    };
    state.completeScenes = finish;
    const deferred = [];
    // Trigger creation already measures the current layout. Only fonts still
    // loading at this point can invalidate those initial measurements.
    const fontsLoading = document.fonts?.status === 'loading';
    try {
      run(initHero);
      for (const family of families) {
        if (family.immediate) {
          prepareSceneLayout(family);
          run(family.init);
        }
        else if (family.sections.length) deferred.push(family);
      }
    } catch (error) {
      finish(false);
      throw error;
    }
    if (!live()) { finish(false); return; }
    const focusedControl = state.focusedControl;
    state.focusedControl = null;
    // Reverting a pin reparents the Hero and can temporarily drop native focus.
    if (focusedControl?.isConnected && !focusedControl.disabled && document.activeElement === document.body) {
      focusedControl.focus({ preventScroll: true });
    }

    let sceneFrame = 0;
    let sceneObserver = null;
    const pendingFamilies = new Set();
    const initializeFamilies = (ready) => {
      if (!live()) { finish(false); return; }
      try {
        for (const family of ready) {
          const index = deferred.indexOf(family);
          if (index < 0) continue;
          deferred.splice(index, 1);
          family.sections.forEach((section) => sceneObserver?.unobserve(section));
          prepareSceneLayout(family);
          run(family.init);
        }
      } catch (error) { finish(false); throw error; }
      if (!deferred.length) { stopWatching(); finish(true); }
    };
    // Fallback / restored-page measurement only, never polling offscreen scenes
    // every scroll frame when the browser can provide intersection geometry.
    const initializeNearby = () => {
      sceneFrame = 0;
      const edge = window.innerHeight * 2;
      const ready = deferred.filter((family) => family.sections.some((section) => {
        const rect = section.getBoundingClientRect();
        return rect.height > 0 && rect.top < edge;
      }));
      initializeFamilies(ready);
    };
    const scheduleNearby = () => {
      if (!sceneFrame && live()) sceneFrame = window.requestAnimationFrame(initializeNearby);
    };
    const observeScenes = () => {
      sceneObserver?.disconnect();
      if (!live() || !deferred.length) return;
      // Viewport bottom plus one viewport = the original two-viewport edge.
      sceneObserver = new IntersectionObserver((entries) => {
        const sections = new Set(entries.filter((entry) => entry.boundingClientRect.height > 0
          && (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight * 2))
          .map((entry) => entry.target));
        const ready = deferred.filter((family) => family.sections.some((section) => sections.has(section)));
        // Rails/reveals use IO's current geometry directly; pin creation retains
        // the original rAF phase. These are the two measured scheduling modes.
        initializeFamilies(ready.filter((family) => family.init !== initMotionFilm));
        ready.filter((family) => family.init === initMotionFilm)
          .forEach((family) => pendingFamilies.add(family));
        // IO supplies cached geometry; scene writes stay in the original rAF
        // phase so pin setup and the following paint are not split by the callback.
        if (pendingFamilies.size && !sceneFrame && live()) {
          sceneFrame = window.requestAnimationFrame(() => {
            sceneFrame = 0;
            const ready = [...pendingFamilies];
            pendingFamilies.clear();
            initializeFamilies(ready);
          });
        }
      }, { rootMargin: '0px 0px ' + window.innerHeight + 'px 0px' });
      deferred.forEach((family) => family.sections.forEach((section) => sceneObserver.observe(section)));
    };
    const stopWatching = () => {
      sceneObserver?.disconnect();
      sceneObserver = null;
      pendingFamilies.clear();
      window.removeEventListener('scroll', scheduleNearby);
      window.removeEventListener('resize', resizeScenes);
      window.removeEventListener('pageshow', scheduleNearby);
      window.cancelAnimationFrame(sceneFrame);
      sceneFrame = 0;
    };
    const resizeScenes = () => {
      if ('IntersectionObserver' in window) observeScenes();
      else scheduleNearby();
    };
    if (deferred.length) {
      if ('IntersectionObserver' in window) observeScenes();
      else window.addEventListener('scroll', scheduleNearby, { passive: true, signal });
      window.addEventListener('resize', resizeScenes, { passive: true, signal });
      window.addEventListener('pageshow', scheduleNearby, { signal });
      addCleanup(stopWatching);
    } else finish(true);
    Promise.resolve(document.fonts?.ready).then(() => {
      if (!live()) return;
      if (fontsLoading) ScrollTrigger?.refresh?.();
      if (deferred.length) scheduleNearby();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true, signal: state.lifecycle.signal });
  else boot();
  window.ACHomeMotion = { destroy, boot };
})();
